<script setup lang="ts">
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import {
    AiChatQuestionTemplatesService,
    type AiChatQuestionTemplate,
    type CustomerUser as User,
    type LawFields,
    LawFieldsService
  } from '@/types/generated'
  import { onMounted, ref, computed } from 'vue'
  import * as yup from 'yup'
  import { useRouter } from 'vue-router'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'
  import { SlashIcon } from '@heroicons/vue/24/outline'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useLawFieldStore } from '@/store/lawFieldStore'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import { CopyIcon } from 'lucide-vue-next'
  import { ROUTES } from '@/router/routes'
  import { useLawTopicTranslation } from '@/composables/useLawTopicTranslation'

  const router = useRouter()
  const template = ref<AiChatQuestionTemplate>()
  const { fetchUser } = useUserStore()
  const user = computed<User>(() => useUserStore().user)
  const lawFields = ref<LawFields | null>(null)
  const { getLawFieldOptions } = useLawFieldStore()
  const lawFieldOptions = computed<Option[]>(() => getLawFieldOptions(user.value))
  const { lawTopicTranslations } = useLawTopicTranslation()

  const lawTopicOptions = computed<Option[]>(() => {
    if (!template.value || !template.value.law_field) {
      return []
    }

    const translations = lawTopicTranslations.value

    return (
      lawFields.value?.data[template.value.law_field]?.map((topic) => ({
        value: topic,
        title: translations[topic] || topic
      })) || []
    ).sort((a, b) => a.title.localeCompare(b.title))
  })

  onMounted(async () => {
    const fetching = [fetchTemplate(), fetchLawFields()]
    if (!user.value) {
      fetching.push(fetchUser())
    }
    await Promise.all(fetching)
  })

  const fetchTemplate = async () => {
    template.value = await AiChatQuestionTemplatesService.aiChatQuestionTemplatesRetrieve({
      uuid: router.currentRoute.value.params.uuid.toString()
    })
  }

  const fetchLawFields = async () => {
    lawFields.value = await LawFieldsService.lawFieldsRetrieve()
  }

  const handleFormSave = async (values: any) => {
    if (!template.value || template.value?.is_jupus_template) return
    await AiChatQuestionTemplatesService.aiChatQuestionTemplatesPartialUpdate({
      uuid: template.value.uuid,
      requestBody: {
        ...values,
        title: template.value.title,
        prompt: template.value.prompt,
        law_field: template.value.law_field,
        law_topic: template.value.law_topic
      }
    })
  }

  const copyTemplate = async () => {
    const newTemplate = await AiChatQuestionTemplatesService.aiChatQuestionTemplatesCopyCreate({
      uuid: template.value.uuid
    })
    await router.push(
      ROUTES.APP.SETTINGS.AI.PROMPT_TEMPLATE_DETAIL.replace(':uuid', newTemplate.uuid)
    )
  }
</script>
<template>
  <SettingsPage v-if="template && user && lawFields">
    <template #breadcrumbs-end>
      <li class="inline-flex items-center truncate text-sm font-semibold text-gray-800">
        <SlashIcon class="size-5 shrink-0 text-gray-500" />
        {{ template.title }}
      </li>
    </template>
    <template #actions>
      <div class="flex items-center space-x-2">
        <button
          class="inline-flex items-center gap-x-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
            font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500
            disabled:pointer-events-none disabled:opacity-50"
          @click="copyTemplate"
        >
          <CopyIcon class="h-5 w-5" />
          Duplizieren
        </button>
      </div>
    </template>
    <div v-if="template.is_jupus_template">
      <div class="mb-4 border-l-4 border-yellow-400 bg-yellow-50 p-4">
        <p class="text-sm font-semibold text-yellow-700">
          JUPUS Vorlagen können nicht direkt bearbeitet werden. Klicken Sie auf "Duplizieren" um
          eine bearbeitbare Kopie zu erstellen.
        </p>
      </div>
    </div>
    <AutoSavingForm @save="handleFormSave">
      <div class="mb-4">
        <InputField
          v-model="template.title"
          name="title"
          label="Titel"
          :rules="yup.string().required()"
          :disabled="template.is_jupus_template"
        />
        <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <SelectField
            v-model="template.law_field"
            :options="lawFieldOptions"
            name="law_field"
            title="Rechtsgebiet"
            label="Rechtsgebiet"
            :allow-unselect="true"
            unselected-option-text="Alle Rechtsgebiete"
            :disabled="template.is_jupus_template"
          />
          <SelectField
            v-model="template.law_topic"
            :options="lawTopicOptions"
            name="law_topic"
            title="Rechtsthema"
            label="Rechtsthema"
            :allow-unselect="true"
            unselected-option-text="Alle Rechtsthemen"
            :disabled="template.is_jupus_template"
          />
        </div>
        <TextAreaField
          v-model="template.prompt"
          name="prompt"
          label="Prompt"
          :disabled="template.is_jupus_template"
          :rules="yup.string().required()"
          rows="10"
        />
      </div>
    </AutoSavingForm>
  </SettingsPage>
</template>
